import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import moment from 'moment'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { createCoupon, updateCoupon } from '../../actions/certificationActions'
import { getAllOrganizations } from '../../actions/organizationsActions'
import { DOCUMENT_BODY_REFERENCE, MODAL_MENU_PORTAL_STYLES } from '../../misc/Constants'
import { isPercentage, showGenericError } from '../../utility/certificationBuilder'
import { useForm } from './useForm'
import Modal from '../common/Modal'
import Input from '../common/form/Input'
import Select from '../common/form/Select'
import Toggle from '../common/form/Toggle'
import CancelButton from '../common/buttons/CancelButton'
import SubmitButton from '../common/buttons/SubmitButton'

import classes from './CouponFormModal.module.scss'

const CODE_PATTERN = /^[A-Z0-9_-]+$/

const CouponFormModal = ({ isOpen, certificationId, coupon, onClose }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const organizations = useSelector(state => state.organizations.organizations)
  const { form, errors, setErrors, setField } = useForm({
    code: coupon?.code || '',
    label: coupon?.label || '',
    percentOff: coupon ? String(coupon.percentOff) : '100',
    maxUses: coupon?.maxUses ? String(coupon.maxUses) : '',
    expiresAt: coupon?.expiresAt ? moment(coupon.expiresAt) : null,
    organizationId: coupon?.organization?.id || null,
    active: coupon ? coupon.active : true,
  })
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (organizations.length === 0) { dispatch(getAllOrganizations()) }
  }, [dispatch, organizations.length])

  const organizationOptions = [
    { value: null, label: t('certificationBuilder.coupons.form.anyOrganization') },
    ...organizations.map(organization => ({ value: organization.id, label: organization.name })),
  ]

  const handleSubmit = (e) => {
    e.preventDefault()
    const code = form.code.trim().toUpperCase()
    const percentOff = Number(form.percentOff)
    if (!CODE_PATTERN.test(code)) { setErrors({ code: t('certificationBuilder.coupons.form.errors.code') }); return }
    if (!isPercentage(form.percentOff)) {
      setErrors({ percentOff: t('certificationBuilder.coupons.form.errors.percentOff') })
      return
    }

    const attributes = {
      code, label: form.label.trim(), percentOff, maxUses: form.maxUses === '' ? null : Number(form.maxUses),
      expiresAt: form.expiresAt ? form.expiresAt.clone().endOf('day').toISOString() : null, organizationId: form.organizationId, active: form.active,
    }
    const request = coupon ? updateCoupon({ certificationId, id: coupon.id, ...attributes }) : createCoupon({ certificationId, ...attributes })

    setIsSubmitting(true)
    dispatch(request).unwrap()
      .then(() => { toast.success(t('certificationBuilder.coupons.form.saveSuccess'), { position: toast.POSITION.TOP_RIGHT }); onClose() })
      .catch((apiErrors) => apiErrors?.code || apiErrors?.percentOff || apiErrors?.maxUses
        ? setErrors({ code: apiErrors.code?.[0], percentOff: apiErrors.percentOff?.[0], maxUses: apiErrors.maxUses?.[0] })
        : showGenericError())
      .finally(() => setIsSubmitting(false))
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} className={classes.modalContent}>
      <h2 className={classes.title}>
        {coupon ? t('certificationBuilder.coupons.form.editTitle', { code: coupon.code }) : t('certificationBuilder.coupons.form.createTitle')}
      </h2>

      <form onSubmit={handleSubmit} className={classNames('d-flex flex-column', classes.form)}>
        <div className={classes.pairRow}>
          <div>
            <label htmlFor='couponCode'>{t('certificationBuilder.coupons.form.code')} <span className='required-text'>*</span></label>
            <Input
              id='couponCode'
              type='text'
              value={form.code}
              placeholder={t('certificationBuilder.coupons.form.codePlaceholder')}
              onChange={(e) => setField('code', e.target.value.toUpperCase())}
              error={errors.code}
            />
          </div>
          <div>
            <label htmlFor='couponPercentOff'>{t('certificationBuilder.coupons.form.percentOff')} <span className='required-text'>*</span></label>
            <Input
              id='couponPercentOff'
              type='number'
              min={1}
              max={100}
              value={form.percentOff}
              endAdornment={<span className={classes.adornment}>%</span>}
              onChange={(e) => setField('percentOff', e.target.value)}
              error={errors.percentOff}
            />
          </div>
        </div>

        <div>
          <label htmlFor='couponLabel'>{t('certificationBuilder.coupons.form.label')}</label>
          <Input
            id='couponLabel'
            type='text'
            value={form.label}
            placeholder={t('certificationBuilder.coupons.form.labelPlaceholder')}
            onChange={(e) => setField('label', e.target.value)}
          />
        </div>

        <div className={classes.pairRow}>
          <div>
            <label htmlFor='couponMaxUses'>{t('certificationBuilder.coupons.form.maxUses')}</label>
            <Input
              id='couponMaxUses'
              type='number'
              min={1}
              value={form.maxUses}
              placeholder={t('certificationBuilder.coupons.form.maxUsesPlaceholder')}
              onChange={(e) => setField('maxUses', e.target.value)}
              error={errors.maxUses}
            />
          </div>
          <div>
            <label htmlFor='couponExpiresAt'>{t('certificationBuilder.coupons.form.expiresAt')}</label>
            <Input
              id='couponExpiresAt'
              type='date'
              value={form.expiresAt}
              placeholder={t('certificationBuilder.coupons.form.expiresPlaceholder')}
              onChange={(date) => setField('expiresAt', date)}
              isClearable
              autoComplete='off'
            />
          </div>
        </div>

        <div>
          <label htmlFor='couponOrganization'>{t('certificationBuilder.coupons.form.organization')}</label>
          <Select
            inputId='couponOrganization'
            options={organizationOptions}
            value={organizationOptions.find(option => option.value === form.organizationId) || null}
            onChange={(option) => setField('organizationId', option.value)}
            isSearchable={false}
            menuPortalTarget={DOCUMENT_BODY_REFERENCE}
            styles={MODAL_MENU_PORTAL_STYLES}
          />
        </div>

        {coupon && (
          <label className={classNames('d-flex align-items-center mb-0', classes.toggle)}>
            <Toggle size='small' checked={form.active} onChange={() => setField('active', !form.active)} />
            {t('certificationBuilder.coupons.form.active')}
          </label>
        )}

        <div className='row pt-3'>
          <div className='col-6'>
            <CancelButton onClick={onClose} buttonMessage={t('common.cancel')} />
          </div>
          <div className='col-6'>
            <SubmitButton type='submit' buttonMessage={t('common.save')} loading={isSubmitting} noTopMargin />
          </div>
        </div>
      </form>
    </Modal>
  )
}

CouponFormModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  certificationId: PropTypes.number.isRequired,
  coupon: PropTypes.shape({
    id: PropTypes.number.isRequired,
    code: PropTypes.string.isRequired,
    label: PropTypes.string,
    percentOff: PropTypes.number.isRequired,
    maxUses: PropTypes.number,
    expiresAt: PropTypes.string,
    active: PropTypes.bool.isRequired,
    organization: PropTypes.shape({ id: PropTypes.number.isRequired }),
  }),
  onClose: PropTypes.func.isRequired,
}

export default CouponFormModal
